import { useEffect, useMemo, useState } from "react"
import { delay } from "../../../features/dataFunctions/dataFunctions"
import { useLazyGetSearchResultsQuery } from "../../../api/rtk/geocoder/slices/geocoderApiSlice"
import { store } from "../../../stateManagement/store"
import MapSearchbarResult from "./MapSearchbarResult"
import { useLazyGetFeatureWFSQuery } from "../../../api/rtk/mapWFS/slices/mapWFSApiSlice"
import { getItemWebStorage } from "../../../features/webStorageSaver"
import { setHighlightObjectGeoJSON, setHighlightObjectStyle, setMapPosition, setPositionsOfGeoMarkers, showToastMessage } from "../../../stateManagement/reducers"
import { useDispatch } from "react-redux"
import SmallLoader from "../../loaders/SmallLoader"
import { checkGeoJSONGeometryIsNotEmpty, formatExtent } from "../../map/mapFeatures/helpers"
import { useLazyFetchPathPositionCoordsQuery } from "../../../api/rtk/base/slices/mapApiSlice"
import { getLineStyle, mapStylesColors } from "../../map/mapFeatures/styles/mapStyles"
import { fromLonLat } from "ol/proj"
import { containsCoordinate } from "ol/extent"

const clearButtonIconSrc = require('../../../assets/images/fontAwesomeIcons/xmark-solid.svg').default

export default function MapSearchbar() {
    const dispatch = useDispatch()
    const [inputSearchValue, setInputSearchValue] = useState<any>('')
    const [searchValue, setSearchValue] = useState<any>('')
    const [inputPathPositionValue, setInputPathPositionValue] = useState<any>('')
    const [pathPositionValue, setPathPositionValue] = useState<any>('')
    const [mergedSearchResults, setMergedSearchResults] = useState<any>([])
    const [searchResultsAreShown, setSearchResultsAreShown] = useState<boolean>(true)
    const [searchPathIsSelected, setSearchPathIsSelected] = useState<boolean>(false)
    const [selectedSearchPath, setSelectedSearchPath] = useState<any>(null);
    const [selectedSearchPathPosition, setSelectedSearchPathPosition] = useState<any>(null)
    const [selectedSearchPathEnding, setSelectedSearchPathEnding] = useState<any>(null)
    const [focusedSearchBarItemIndex, setFocusedSearchBarItemIndex] = useState<any>(null)
    const [fetchSearchResults, { isError: isErrorSearchRequest, isFetching: isFetchingSearchRequest }] = useLazyGetSearchResultsQuery()
    const [getFeatureWFS, { isError: isErrorFeatureWFS, isFetching: isFetchingFeatureWFS }] = useLazyGetFeatureWFSQuery()
    const [fetchPathPositionCoords, { isError: isErrorPathPositionCoords, isFetching: isFetchingPathPositionCoords }] = useLazyFetchPathPositionCoordsQuery()

    const formattedLocalMapExtent: any = useMemo(() => {
        const currentConfig = getItemWebStorage('currentConfig')
        const localExtent = currentConfig.mapDefaults.extent
        const extent = formatExtent(localExtent)
        return extent
    }, [])


    // Изменение значение для поиска

    useEffect(() => {
        if (focusedSearchBarItemIndex === -1) {
            const trimmedInputValue = inputSearchValue.trim()
            delay(() => setSearchValue(trimmedInputValue), 300)
        }
    }, [inputSearchValue])

    useEffect(() => {
        if (searchValue) {
            getMergedSearchResults()
            setSearchResultsAreShown(true)
        } else {
            setMergedSearchResults([])
        }
    }, [searchValue])

    // Получение результатов поиска

    async function getMergedSearchResults() {
        const searchResultsFromPathsList = getSearchResultsFromPathsList()
        const searchResultFromCoordinates = getSearchResultFromCoordinates()
        const searchResultsFromRequest = await getSearchResultsFromRequest()
        const mergedSearchResults = [...searchResultsFromPathsList, ...searchResultsFromRequest]
        if (searchResultFromCoordinates) mergedSearchResults.unshift(searchResultFromCoordinates)
        setMergedSearchResults(mergedSearchResults)
    }

    function getSearchResultsFromPathsList() {
        const pathsList = store.getState().pathsInfo.pathsList
        const pathsListSearchFilter = (path: any, searchValue: any) => {
            const lowerName = path?.Name?.toLowerCase()
            const lowerRoadName = path?.RoadName?.toLowerCase()
            const lowerSearchValue = searchValue?.toLowerCase()
            return `${lowerRoadName} ${lowerName}`.includes(lowerSearchValue)
        }
        const searchResults = pathsList.filter((path: any) => pathsListSearchFilter(path, searchValue))
        return searchResults
    }

    async function getSearchResultsFromRequest() {
        const response = await fetchSearchResults({ name: searchValue })
        let searchResults = response?.data
        if (formattedLocalMapExtent) {
            searchResults = searchResults.filter(({ lat, lon }: any) => containsCoordinate(formattedLocalMapExtent, fromLonLat([lon, lat])))
        }
        return searchResults
    }

    function getSearchResultFromCoordinates() {
        const matches = searchValue.match(/-?\d+(?:[.,]\d+)?/g)
        if (matches) {
            const [latitude, longitude] = matches.map(Number)
            if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
                return {
                    lat: latitude.toFixed(8),
                    lon: longitude.toFixed(8),
                    name: `${latitude.toFixed(2)} с. ш. ${longitude.toFixed(2)} в. д.`
                };
            }
        }
    }

    // Переход к результату поиска

    function handleClickOnSearchResult(searchResult: any, index: number) {
        setFocusedSearchBarItemIndex(index)
        moveMapToSearchResult(searchResult)
    }

    async function moveMapToSearchResult(searchResult: any, afterMouseClick: boolean = true) {
        const searchResultIsPath = !!searchResult?.PathID
        let searchResultName = ''
        if (searchResultIsPath) {
            const { Name, RoadName, PathID, RoadId } = searchResult ?? {}
            searchResultName = `${RoadName} ${Name}`
            const { isSuccessful } = await moveMapToPath(PathID)
            if (isSuccessful) {
                setSelectedSearchPath({ pathId: PathID, roadId: RoadId })
                setSearchPathIsSelected(true)
                setInputSearchValue(searchResultName)
                if (afterMouseClick) {
                    setSearchResultsAreShown(false)
                }
            }
        } else {
            const { lat, lon, name } = searchResult ?? {}
            const coordinates = fromLonLat([lon, lat])
            if (containsCoordinate(formattedLocalMapExtent, coordinates)) {
                searchResultName = name
                const newMapPosition = { latitude: lat, longitude: lon, zoom: 11 }
                moveMapToPosition(newMapPosition)
                setSearchPathIsSelected(false)
                setSelectedSearchPath(null)
                setInputSearchValue(searchResultName)
                if (afterMouseClick) {
                    setSearchResultsAreShown(false)
                }
            } else {
                store.dispatch(showToastMessage({ messageText: 'Данное местоположение находится вне зоны используемого сервера', messageType: "error", windowType: "map" }))
            }
        }
    }

    async function moveMapToPath(pathId: any) {
        const { workspace } = getItemWebStorage('currentConfig')
        const cqlFilter = `PathID = ${pathId}`
        const { data: pathGeoJSON } = await getFeatureWFS({ workspace, layer: 'MALink', cqlFilter })
        if (checkGeoJSONGeometryIsNotEmpty(pathGeoJSON)) {
            dispatch(setHighlightObjectGeoJSON(pathGeoJSON))
            dispatch(setHighlightObjectStyle(getLineStyle(mapStylesColors?.mustard, 4)))
            getPathPositionAndEnding(pathGeoJSON)
            dispatch(setPositionsOfGeoMarkers([]))
            setInputPathPositionValue('')
            setPathPositionValue('')
            return { isSuccessful: true }
        } else {
            dispatch(showToastMessage({ messageText: 'Объект содержит пустую геометрию', messageType: "error", windowType: "map" }))
            return { isSuccessful: false }
        }
    }

    function moveMapToPosition(mapPosition: any) {
        dispatch(setMapPosition(mapPosition))
        dispatch(setPositionsOfGeoMarkers([mapPosition]))
        dispatch(setHighlightObjectGeoJSON(null))
    }

    function handleEnterPressOnPathPositionValueInput(event: any) {
        const keyCode = event.keyCode
        if (keyCode === 13) {
            checkFormatPathPositionValue(inputPathPositionValue)
        } else if (keyCode === 39) {
            setFocusedSearchBarItemIndex(-1)
        }
    }

    function getPathPositionAndEnding(pathGeoJSON: any) {
        const minPosition = Math.min(...pathGeoJSON.features.map((o: any) => o.properties.Position))
        const maxEnding = Math.max(...pathGeoJSON.features.map((o: any) => o.properties.Ending))
        if (minPosition) {
            setSelectedSearchPathPosition(minPosition.toFixed(3))
        }
        if (maxEnding) {
            setSelectedSearchPathEnding(maxEnding.toFixed(3))
        }
    }

    // Проверка формата значения км+ на дороге

    function checkFormatPathPositionValue(pathPositionValue: any) {
        const regExpOfKmPlus = /^\d+\+\d{0,3}$/
        const regExpOfKmMinus = /^\d+\-\d{0,3}$/
        const regExpOfKm = /^\d+$/
        let formattedPathPositionValue = ''
        if (regExpOfKmPlus.test(pathPositionValue)) {
            const [km, m]: [string, string] = pathPositionValue.split('+')
            formattedPathPositionValue = `${km}+${m.padStart(3, '0')}`
            setInputPathPositionValue(formattedPathPositionValue)
            setPathPositionValue(formattedPathPositionValue)
        } else if (regExpOfKmMinus.test(pathPositionValue)) {
            const [km, m]: [string, string] = pathPositionValue.split('-')
            formattedPathPositionValue = `${km}-${m.padStart(3, '0')}`
            setInputPathPositionValue(formattedPathPositionValue)
            setPathPositionValue(formattedPathPositionValue)
        } else if (regExpOfKm.test(pathPositionValue)) {
            formattedPathPositionValue = `${pathPositionValue}+000`
            setInputPathPositionValue(formattedPathPositionValue)
            setPathPositionValue(formattedPathPositionValue)
        } else {
            dispatch(showToastMessage({ messageText: "Значение Км+ должно соответствовать формату км+м", messageType: "error", windowType: "map" }))
        }
    }

    // Переход к км+ на дороге

    useEffect(() => {
        if (pathPositionValue) {
            (async () => {
                try {
                    await moveMapToPositionOnPath(); // бросает наружу
                } catch (error: any) {
                    dispatch(showToastMessage({ messageText: error.message, messageType: "error", windowType: "map" }))
                }
            })();
        }
    }, [pathPositionValue]);

    async function moveMapToPositionOnPath() {
        const { pathId, roadId } = selectedSearchPath
        let response;
        try {
            const { data: maintenancePositionResponse }: any = await fetchPathPositionCoords({ roadId, pathId, position: pathPositionValue })
            if (Object.hasOwn(maintenancePositionResponse, 'Error') && maintenancePositionResponse.Error === "Invalid maintenance position") {
                throw new Error("Эксплуатационный километр не найден. Производится поиск по проектному километру.")
            }
            response = maintenancePositionResponse;
        } catch (error: any) {
            dispatch(showToastMessage({ messageText: error.message, messageType: "error", windowType: "map" }))
            const projectPathPosition = pathPositionValue.replace('+', '.');
            const { data: projectPositionResponse }: any = await fetchPathPositionCoords({ roadId, pathId, position: projectPathPosition })
            if (Object.hasOwn(projectPositionResponse, 'Error')) {
                let messageText = 'Выбранная дорогая не проходит через указанное положение.'
                if (selectedSearchPathPosition && selectedSearchPathEnding) {
                    messageText = `Выбранная дорогая не проходит через указанное положение. Выберите положение в приблизительном промежутке  от ${selectedSearchPathPosition} до ${selectedSearchPathEnding}.`
                }
                throw new Error(messageText)
            }
            response = projectPositionResponse;
        }
        const { Latitude, Longitude } = response ?? {}
        const mapPosition = { latitude: Latitude, longitude: Longitude, zoom: 11 }
        dispatch(setMapPosition(mapPosition))
        dispatch(setPositionsOfGeoMarkers([mapPosition]))
    }

    // Управление с помощью клавиатуры

    useEffect(() => {
        if (focusedSearchBarItemIndex != null) {
            if (focusedSearchBarItemIndex >= 0 && searchResultsAreShown) {
                const focusedSearchBarItemDiv: any = document.getElementById("map-searchbar-item-focused")
                const searchResult = mergedSearchResults[focusedSearchBarItemIndex]
                focusedSearchBarItemDiv.focus()
                moveMapToSearchResult(searchResult, false)
            } else if (focusedSearchBarItemIndex === -1) {
                const focusedSearchBarItemDiv: any = document.getElementById("map-searchbar-input")
                focusedSearchBarItemDiv.focus()
                setSearchResultsAreShown(true)
            } else if (focusedSearchBarItemIndex === -2) {
                const focusedSearchBarItemDiv: any = document.getElementById("map-path-position-input")
                focusedSearchBarItemDiv.focus()
            }
        }
    }, [focusedSearchBarItemIndex])

    function handleKeyPressOnSearchValueInput(event: any) {
        const keyCode = event?.keyCode
        if (keyCode === 40 && searchResultsAreShown && mergedSearchResults.length > 0) {
            setFocusedSearchBarItemIndex(0)
            event.preventDefault()
        }
    }

    function handleKeyPressOnSearchResult(event: any, index: number) {
        event.preventDefault()
        const keyCode = event?.keyCode
        if (keyCode === 40) {
            if (index !== mergedSearchResults.length - 1) {
                setFocusedSearchBarItemIndex(index + 1)
            } else {
                setFocusedSearchBarItemIndex(0)
            }
        }
        if (keyCode === 38) {
            if (index !== 0) {
                setFocusedSearchBarItemIndex(index - 1)
            } else {
                setFocusedSearchBarItemIndex(-1)
            }
        }
        if (keyCode === 37 && searchPathIsSelected) {
            setFocusedSearchBarItemIndex(-2)
        }
    }

    // Очистка поисковой строки

    function clearSearchValue() {
        setInputSearchValue('')
        setSearchValue('')
        setSearchPathIsSelected(false)
        setSelectedSearchPath(null)
        dispatch(setPositionsOfGeoMarkers([]))
        dispatch(setHighlightObjectGeoJSON(null))
    }

    useEffect(() => {
        setMapSearchbarOuterClickHandle()
    }, [])

    function setMapSearchbarOuterClickHandle() {
        document.onclick = function (e: any) {
            if (e.target.id != "map-searchbar-input") {
                setSearchResultsAreShown(false)
            };
        };
    }

    return (
        <div className="map-searchbar-div">
            <div className="map-searchbar-inputs-div">
                {(isFetchingSearchRequest || isFetchingFeatureWFS) &&
                    <SmallLoader
                        iconColor="black"
                        className={selectedSearchPath ? "with-path-position-input" : ''}
                    />
                }
                {searchPathIsSelected &&
                    <input
                        id="map-path-position-input"
                        value={inputPathPositionValue}
                        onChange={(e: any) => setInputPathPositionValue(e.target.value)}
                        onFocus={() => setSearchResultsAreShown(false)}
                        onKeyDown={handleEnterPressOnPathPositionValueInput}
                        autoComplete="off"
                        placeholder="Км+"
                    />
                }
                <input
                    id="map-searchbar-input"
                    value={inputSearchValue}
                    onClick={() => setSearchResultsAreShown(true)}
                    onChange={(e: any) => setInputSearchValue(e.target.value)}
                    onFocus={() => setFocusedSearchBarItemIndex(-1)}
                    onKeyDown={handleKeyPressOnSearchValueInput}
                    autoComplete="off"
                    placeholder="Введите название населённого пункта, название дороги или координаты"
                />
                <button className="map-searchbar-clear-button" onClick={clearSearchValue}>
                    <img src={clearButtonIconSrc} />
                </button>
            </div>
            {searchResultsAreShown &&
                <div className="map-searchbar-results-div" onScroll={(event) => event.preventDefault()}>
                    {mergedSearchResults.map((searchResult: any, index: any) =>
                        <MapSearchbarResult
                            searchResult={searchResult}
                            onClick={() => handleClickOnSearchResult(searchResult, index)}
                            onKeyDown={(event: any) => handleKeyPressOnSearchResult(event, index)}
                            focused={index === focusedSearchBarItemIndex}
                            index={index}
                            searchValue={searchValue}
                        />
                    )}
                </div>
            }
        </div>
    )
}
